import { z } from "zod/v4";

import { UpsRateRequestSchema } from "./catalog";

// ---------------------------------------------------------------------------
// One integration-client call crossing the user-code ↔ trusted boundary, and
// the SDK-read Lambda's event/response shape for the custom-shipping path.
// (Agentic Logic rules carry the same `call` inside an SDK-read operation of
// kind `integrationClient`; see functions/agentic-logic/runtime/sdk-read.)
// ---------------------------------------------------------------------------

/** Discriminated on `client`; each member pins the operation name and typed args. */
export const AgenticLogicIntegrationClientCallSchema = z.discriminatedUnion(
  "client",
  [
    z
      .object({
        client: z.literal("ups"),
        operation: z.literal("rate"),
        args: UpsRateRequestSchema,
      })
      .strict(),
  ]
);

export type AgenticLogicIntegrationClientCall = z.infer<
  typeof AgenticLogicIntegrationClientCallSchema
>;

/**
 * The SDK-read Lambda event for a custom-shipping invocation. Structurally
 * disjoint from the Agentic Logic read events (`{capability, operation}` /
 * `{capability, operations}`), which is what lets one handler route by shape.
 */
export const AgenticLogicIntegrationClientCallEventSchema = z
  .object({
    capability: z.string().min(1),
    call: AgenticLogicIntegrationClientCallSchema,
  })
  .strict();

export type AgenticLogicIntegrationClientCallEvent = z.infer<
  typeof AgenticLogicIntegrationClientCallEventSchema
>;

export const AgenticLogicIntegrationClientCallErrorCodeList = [
  "invalid_request",
  "unauthorized",
  /** The org has no connected integration for this client (or it is degraded). */
  "integration_not_connected",
  /** The capability does not grant this client/operation, or the operation is unknown. */
  "operation_not_allowed",
  "timeout",
  /** The third party answered with an error; `message` carries its wording. */
  "upstream_error",
  "call_failed",
] as const;

export const AgenticLogicIntegrationClientCallErrorCodeSchema = z.enum(
  AgenticLogicIntegrationClientCallErrorCodeList
);

export type AgenticLogicIntegrationClientCallErrorCode =
  (typeof AgenticLogicIntegrationClientCallErrorCodeList)[number];

export const AGENTIC_LOGIC_INTEGRATION_CLIENT_ERROR_MESSAGE_MAX_LENGTH = 300;

export const AgenticLogicIntegrationClientCallResponseSchema =
  z.discriminatedUnion("ok", [
    z
      .object({
        ok: z.literal(true),
        result: z.unknown(),
      })
      .strict(),
    z
      .object({
        ok: z.literal(false),
        error: z
          .object({
            code: AgenticLogicIntegrationClientCallErrorCodeSchema,
            /** Populated only for `upstream_error` (the third party's own wording, capped). */
            message: z
              .string()
              .max(AGENTIC_LOGIC_INTEGRATION_CLIENT_ERROR_MESSAGE_MAX_LENGTH)
              .optional(),
          })
          .strict(),
      })
      .strict(),
  ]);

export type AgenticLogicIntegrationClientCallResponse = z.infer<
  typeof AgenticLogicIntegrationClientCallResponseSchema
>;
